import Phrase from '../../libs/translationsUtils/phrase'
import List from '../menu/list'
import Link, { CategoryLink } from '../menu/link'
import Dropdown from '../dropdown/index'
import { CategoryInfo, isParentCategoryInfo, ParentCategoryInfo } from './Types'
import Icon from '../assets'
// @ts-expect-error   // fix this by defining the mapping to 'downshift' in tsconfig
import Downshift from 'downshift/preact'
import { ControllerStateAndHelpers } from 'downshift'
import { TranslationCategory } from '../../libs/translationsUtils/translations'
import { ModulesContext } from '../../utils/globals'
import { FunctionalComponent } from 'preact'

export interface MenuQuicklink {
  url: string
  phrase: {
    m: TranslationCategory
    p: string
  }
}

interface TriggerProps {
  href: string
  name: string
  isLink: boolean
}

const Trigger: FunctionalComponent<TriggerProps> = ({ href, name, isLink }) => {
  if (isLink) {
    return (
      <a style='color: inherit' href={href}>
        {name}
      </a>
    )
  }
  return <span>{name}</span>
}

interface Props {
  openOnHover: boolean
  name: string
  categories: ParentCategoryInfo[]
  popupName: string
  quickLinks: Array<MenuQuicklink | null>
}

function isNotNull(i: MenuQuicklink | null): i is MenuQuicklink {
  return !!i
}

const trackParentCategory = (title: string, url: string) => {
  return {
    type: 'Mega Menu click on parent category',
    category_title: title,
    category_url: url,
    click_source: 'menu',
    category_type: 'parent category',
    event_source: 'control'
  }
}

const gaEvents = (url: string) => {
  return {
    eventCategory: 'Homepage',
    eventAction: 'Menu clicked',
    eventLabel: {
      Link: url
    }
  }
}

const trackCategory = (title: string, url: string) => {
  return {
    type: 'Mega Menu click on category',
    category_title: title,
    category_url: url,
    click_source: 'menu',
    category_type: 'category',
    event_source: 'control'
  }
}

const CategoryMenu = ({ categories, name, popupName, quickLinks, openOnHover }: Props) => (
  <Downshift
    onChange={(item: { url: string }) => {
      window.location.href = item.url
    }}
  >
    {({
      getItemProps,
      getInputProps,
      getRootProps,
      highlightedIndex,
      selectHighlightedItem,
      setHighlightedIndex
    }: ControllerStateAndHelpers<{ url: string }>) => {
      const quickLinksClean = quickLinks.filter(isNotNull)
      const categoryList = categories.reduce<Array<CategoryInfo | ParentCategoryInfo>>(
        (categoryList, parentCategory) => {
          return categoryList.concat(parentCategory).concat(parentCategory.subforums || [])
        },
        []
      )
      const isHighlighted = (idx: number) => idx === highlightedIndex

      const onGlobalKey = (direction: 'up' | 'down' | 'enter') => {
        if (direction === 'enter') return selectHighlightedItem()
        if (highlightedIndex === null) return setHighlightedIndex(0)
        let newIndex = highlightedIndex + (direction === 'up' ? -1 : 1)
        if (newIndex < 0) newIndex = 0
        const maxIndex = quickLinksClean.length + categoryList.length - 1
        if (newIndex >= maxIndex) newIndex = maxIndex
        setHighlightedIndex(newIndex)
      }

      const rootProps = getRootProps({ refKey: 'menu-trigger' }, { suppressRefError: true })

      return (
        <ModulesContext.Consumer>
          {(modules) => (
            <Dropdown
              popupName={popupName}
              class='dropdown dropdown--forums-overview'
              onGlobalKey={onGlobalKey}
              openOnHover={modules.includes('MegaMenuUXchanges') && openOnHover}
              trigger={
                <span
                  style={{ display: 'flex', alignItems: 'center' }}
                  class='main-menu-trigger'
                  {...rootProps}
                  {...getInputProps()}
                >
                  <Trigger
                    isLink={Boolean(
                      modules.includes('MegaMenuUXchanges') && openOnHover && quickLinksClean.length
                    )}
                    href={quickLinksClean[0].url}
                    name={name}
                  />
                  <Icon icon='chevronDown' width={16} height={16} />
                </span>
              }
            >
              <li aria-hidden='true' class='arrow is-hidden-S' />

              <li class='main-menu-list--overflow-scroll'>
                {Boolean(quickLinksClean.length) && (
                  <List
                    class='main-menu-list--quicklinks'
                    getItemProps={(idx) => {
                      return getItemProps({
                        item: quickLinksClean[idx],
                        class: `main-menu-list__item main-menu-list__item--no-hover ${
                          isHighlighted(idx) ? 'main-menu-list__item--highlighted' : ''
                        }`
                      })
                    }}
                  >
                    {quickLinksClean.map((item) => {
                      return (
                        <Link
                          track={{
                            type: `Mega Menu click on ${item.phrase.p}`,
                            event_source: 'control'
                          }}
                          key={item.url}
                          href={item.url}
                        >
                          <Phrase {...item.phrase} />
                        </Link>
                      )
                    })}
                  </List>
                )}

                {Boolean(categoryList.length) && (
                  <List
                    getItemProps={(idx) => {
                      return getItemProps({
                        item: categoryList[idx],
                        class: `main-menu-list__item main-menu-list__item--no-hover ${
                          isHighlighted(quickLinksClean.length + idx)
                            ? 'main-menu-list__item--highlighted'
                            : ''
                        }`
                      })
                    }}
                  >
                    {categoryList.map((category) => {
                      const isParent = isParentCategoryInfo(category)
                      return (
                        <CategoryLink
                          key={category.id}
                          track={(isParent ? trackParentCategory : trackCategory)(
                            category.title,
                            category.url
                          )}
                          gaEvents={gaEvents(category.url)}
                          href={category.url}
                          title={category.title}
                          topicsCount={isParent ? undefined : category.topicCount}
                        >
                          {isParent ? <strong>{category.title}</strong> : category.title}
                        </CategoryLink>
                      )
                    })}
                  </List>
                )}
              </li>
            </Dropdown>
          )}
        </ModulesContext.Consumer>
      )
    }}
  </Downshift>
)

export default CategoryMenu
